/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { Search, SelectOption, SilentSelectField, breakpoints } from '@coursera/cds-core';

import _t from 'i18n!nls/skills-common';

const FILTER_ALL = 'all';
const TIME_PERIODS = [30, 60, 90];

export type TrendingSkillsFilters = {
  search: { value: string; onChange: (value: string) => void };
  programLearners: { value: string; onChange: (value: string) => void };
  skillDomains: { value: string; onChange: (value: string) => void };
  timePeriod: { value: number; onChange: (value: number) => void };
};

const useTrendingSkillsFilters = (): TrendingSkillsFilters => {
  const search = useState('');
  const programLearners = useState(FILTER_ALL);
  const skillDomains = useState(FILTER_ALL);
  const timePeriod = useState(TIME_PERIODS[2]); // 90 days
  return {
    search: { value: search[0], onChange: search[1] },
    programLearners: { value: programLearners[0], onChange: programLearners[1] },
    timePeriod: { value: timePeriod[0], onChange: timePeriod[1] },
    skillDomains: { value: skillDomains[0], onChange: skillDomains[1] },
  };
};

const getSkillDomains = () => [
  {
    id: FILTER_ALL,
    label: _t('All Skill domains'),
  },
  {
    id: 'personal-development',
    label: _t('Personal Development'),
  },
  {
    id: 'business',
    label: _t('Business'),
  },
  {
    id: 'physical-science-and-engineering',
    label: _t('Physical Science And Engineering'),
  },
  {
    id: 'data-science',
    label: _t('Data Science'),
  },
  {
    id: 'computer-science',
    label: _t('Computer Science'),
  },
  {
    id: 'math-and-logic',
    label: _t('Math And Logic'),
  },
  {
    id: 'information-technology',
    label: _t('Information Technology'),
  },
  {
    id: 'language-learning',
    label: _t('Language Learning'),
  },
  {
    id: 'life-sciences',
    label: _t('Health'),
  },
  {
    id: 'social-sciences',
    label: _t('Social Sciences'),
  },
  {
    id: 'arts-and-humanities',
    label: _t('Arts And Humanities'),
  },
];

const styles = {
  container: css`
    width: 100%;
    display: flex;
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-300);
    flex-wrap: wrap;
    gap: var(--cds-spacing-100) var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      flex-direction: column;
    }
  `,
  searchBar: css`
    margin-right: auto;
    ${breakpoints.down('md')} {
      flex-basis: 100%;
    }
  `,
};

type Props = {
  filters: TrendingSkillsFilters;
  programs: {
    id: string;
    name: string;
  }[];
};

export default ({ filters, programs }: Props) => {
  const allPrograms = [
    {
      id: FILTER_ALL,
      name: _t('All Programs'),
    },
    ...programs,
  ];
  return (
    <div css={styles.container}>
      <Search css={styles.searchBar} placeholder={_t('Search by skill name')} {...filters.search} />

      <SilentSelectField
        label={_t('Include learners from')}
        labelPlacement="integrated"
        value={filters.programLearners.value}
        onChange={(e) => filters.programLearners.onChange(e.target.value as string)}
      >
        {allPrograms.map((program) => (
          <SelectOption key={program.id} value={program.id}>
            {program.name}
          </SelectOption>
        ))}
      </SilentSelectField>

      <SilentSelectField
        label={_t('Time period')}
        labelPlacement="integrated"
        value={filters.timePeriod.value}
        onChange={(e) => filters.timePeriod.onChange(+(e.target.value as string))}
      >
        {TIME_PERIODS.map((days) => (
          <SelectOption key={days} value={days}>
            {_t('Last #{days} days', { days })}
          </SelectOption>
        ))}
      </SilentSelectField>

      <SilentSelectField
        label={_t('Skill domains')}
        labelPlacement="integrated"
        value={filters.skillDomains.value}
        onChange={(e) => filters.skillDomains.onChange(e.target.value as string)}
      >
        {getSkillDomains().map((domain) => (
          <SelectOption key={domain.id} value={domain.id}>
            {domain.label}
          </SelectOption>
        ))}
      </SilentSelectField>
    </div>
  );
};

export { useTrendingSkillsFilters, FILTER_ALL, TIME_PERIODS };
